import React from "react";
import { toast } from "sonner";
import { useAuth } from "@/Admin";
import { ThemedPhoneInput } from "@/components/ui/ThemedPhoneInput";

/**
 * ContactForm — the "Your details" glass card at the top of checkout.
 * Contains the sign-in/create-account/guest strip, plus the first/last
 * name, email and phone inputs. Contact fields are lifted up to the
 * parent so they can also be sent to Stripe as billing_details.
 */
export const ContactForm = ({
  firstName, setFirstName, lastName, setLastName, email, setEmail, phone, setPhone,
  authChoice, setAuthChoice, authMode, setAuthMode,
}) => {
  const { user, login, signup } = useAuth();
  const [authBusy, setAuthBusy] = React.useState(false);
  const [authPassword, setAuthPassword] = React.useState("");

  const doInlineAuth = async (e) => {
    e.preventDefault();
    if (!email || !authPassword) { toast.error("Enter email + password first."); return; }
    if (authMode === "signup" && (!firstName || !lastName)) {
      toast.error("Please enter your first and last name above before creating an account.");
      return;
    }
    setAuthBusy(true);
    try {
      if (authMode === "signup") {
        const data = await signup(email, authPassword, `${firstName} ${lastName}`.trim(), phone, "", firstName, lastName);
        toast.success(`Welcome, ${data.name}!`);
        setAuthChoice("signed_in");
      } else {
        const data = await login(email, authPassword);
        if (data.role !== "customer") {
          toast.error(`That's a ${data.role} account — sign in as a customer to book.`);
        } else {
          toast.success(`Welcome back, ${data.name || data.email}!`);
          setAuthChoice("signed_in");
        }
      }
      setAuthPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || (authMode === "signup" ? "Sign-up failed. Try signing in instead." : "Sign-in failed. Check your password."));
    } finally {
      setAuthBusy(false);
    }
  };

  return (
    <div className="glass p-5 sm:p-6">
      <h2 className="font-serif text-2xl text-[#FFD166] mb-4">Your details</h2>

      <div className="mb-5" data-testid="booking-auth-strip">
        {user && user.role === "customer" ? (
          <div className="rounded-lg border border-[#FFD166]/30 bg-[#FFD166]/5 px-4 py-3 text-sm text-[#F6F1E4] flex items-center gap-2" data-testid="booking-signed-in">
            <span className="text-[#FFD166]">✓</span>
            Booking as <b className="text-[#FFD166]">{user.name || user.email}</b>. Your details are pre-filled.
          </div>
        ) : (
          <div className={`rounded-lg border-2 px-4 py-3 text-sm transition-colors ${authChoice ? "border-[#7EE787]/40 bg-[#7EE787]/5" : "border-[#F4A93A] bg-[#F4A93A]/5"}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium text-[#F6F1E4]">
                {authChoice ? "✓ Ready to book" : "How would you like to check out?"}
                <span className="text-[#F4A93A] ml-1">*</span>
              </span>
              {!authChoice && <span className="text-[10px] uppercase tracking-wider text-[#F4A93A]">Required</span>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => { setAuthChoice(null); setAuthMode("signin"); }} className={`text-xs px-3 py-1.5 rounded-full border transition ${authMode === "signin" ? "bg-[#FFD166] text-[#0A2438] border-[#FFD166]" : "border-[#FFD166]/40 text-[#FFD166] hover:bg-[#FFD166]/10"}`} data-testid="auth-toggle-signin">Sign in</button>
              <button type="button" onClick={() => { setAuthChoice(null); setAuthMode("signup"); }} className={`text-xs px-3 py-1.5 rounded-full border transition ${authMode === "signup" ? "bg-[#FFD166] text-[#0A2438] border-[#FFD166]" : "border-[#FFD166]/40 text-[#FFD166] hover:bg-[#FFD166]/10"}`} data-testid="auth-toggle-signup">Create account</button>
              <button type="button" onClick={() => { setAuthMode("guest"); setAuthChoice("guest"); setAuthPassword(""); }} className={`text-xs px-3 py-1.5 rounded-full border transition ${authChoice === "guest" ? "bg-[#7EE787] text-[#0A2438] border-[#7EE787]" : "border-[#B9C8D5]/40 text-[#B9C8D5] hover:bg-[#B9C8D5]/10"}`} data-testid="auth-toggle-guest">Continue as guest ✨</button>
            </div>
            {authMode !== "guest" && (
              <div className="flex flex-col gap-2 mt-3" data-testid="inline-auth-form">
                <div className="flex flex-col sm:flex-row gap-2">
                  <input type="email" required placeholder="Email" className="input flex-1" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="inline-auth-email" />
                  <input type="password" required minLength={8} placeholder="Password" className="input flex-1" value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} data-testid="inline-auth-password" />
                  <button type="button" onClick={doInlineAuth} disabled={authBusy} className="btn-primary" data-testid="inline-auth-submit">
                    {authBusy ? "…" : (authMode === "signup" ? "Create" : "Sign in")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <input required className="input" placeholder="First name *" value={firstName} onChange={(e) => setFirstName(e.target.value)} data-testid="booking-first-name" />
        <input required className="input" placeholder="Last name *" value={lastName} onChange={(e) => setLastName(e.target.value)} data-testid="booking-last-name" />
      </div>
      <input required className="input mt-4" type="email" placeholder="Email address *" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="booking-email" />
      <div className="mt-4">
        <ThemedPhoneInput
          value={phone}
          onChange={setPhone}
          required
          placeholder="Phone * (we'll text you delivery updates)"
          data-testid="booking-phone"
        />
      </div>
    </div>
  );
};

export default ContactForm;
